import { useMemo } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { Button, Collapse, Space, Switch, Table, Typography } from "antd";
import { DeleteOutlined, LockOutlined, PlusOutlined } from "@ant-design/icons";
import type { MetricOption, SubjectEntity, TableComponentSpec } from "../../Processing/types";
import { GROUPBY_OPTIONS } from "../constants";
import { formatSeriesLabel } from "../../Processing/seriesLabel";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import { PreviewShell } from "../shared/PreviewShell";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";
import { DateRangeForm } from "../shared/DateRangeForm";
import { resolveDateRange } from "../../Utils/resolveDateRange";
import { PlayersForm } from "../shared/PlayersForm";
import { GroupByBar } from "../shared/GroupByBar";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { TableColumnForm } from "./TableColumnForm";
import { isSectionBindable } from "../../Processing/controllerBindings";
import { SectionSummary } from "../shared/SectionSummary";
import {
  tableReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
} from "./tableState";

const { Panel } = Collapse;
const { Text } = Typography;


export function TableConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<TableComponentSpec>) {
  const [state, dispatch] = useConfiguratorSync({
    reducer: tableReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  const MOCK_PLAYER_POSITIONS: Record<string, string> = {
    "Player 1": "Forwards",
    "Player 2": "Forwards",
    "Player 3": "Midfielders",
    "Player 4": "Midfielders",
    "Player 5": "Defenders",
  };

  const previewData = useMemo(() => {
    const configured = state.columns.filter((c) => c.selectedMetricId != null);
    if (configured.length === 0) return null;

    const showSeparators = state.separateByPosition && state.groupBy === "player";

    const antdColumns = [
      {
        key: "category",
        title: GROUPBY_OPTIONS.find((o) => o.value === state.groupBy)?.label ?? "",
        dataIndex: "category",
        render: (value: unknown, record: Record<string, unknown>) => {
          if (record.__is_position_separator) {
            return <span style={{ fontWeight: 700 }}>{String(value ?? "")}</span>;
          }
          return String(value ?? "");
        },
      },
      ...configured.map((c) => {
        const metric = metrics.find((m) => m.id === c.selectedMetricId);
        // Mirror TableWidget's per-column font-color override so the preview matches the render.
        const fontColor = c.enableColorOverride && c.colorOverride ? c.colorOverride : undefined;
        return {
          key: c.id,
          title: c.customName.trim()
            ? c.customName.trim()
            : formatSeriesLabel(metric?.name ?? "Metric", c.metricAgg, "metric"),
          dataIndex: c.id,
          render: (value: unknown, record: Record<string, unknown>) => {
            if (record.__is_position_separator) {
              return value != null ? (
                <span style={{ color: "rgba(0,0,0,0.45)" }}>{String(value)}</span>
              ) : null;
            }
            if (value == null) return null;
            return fontColor ? (
              <span style={{ color: fontColor, fontWeight: 600 }}>{String(value)}</span>
            ) : (
              String(value)
            );
          },
        };
      }),
    ];

    const MOCK_CATEGORIES: Record<string, string[]> = {
      player: ["Player 1", "Player 2", "Player 3", "Player 4", "Player 5"],
      position: ["Position 1", "Position 2", "Position 3", "Position 4", "Position 5"],
      drill: ["Drill 1", "Drill 2", "Drill 3", "Drill 4", "Drill 5"],
      day: ["03-13", "03-14", "03-15", "03-16", "03-17"],
      week: ["2026-W10", "2026-W11", "2026-W12", "2026-W13", "2026-W14"],
      month: ["2025-12", "2026-01", "2026-02", "2026-03", "2026-04"],
      year: ["2022", "2023", "2024", "2025", "2026"],
    };
    const mockNames = MOCK_CATEGORIES[state.groupBy] ?? MOCK_CATEGORIES.player;
    const dataRows = mockNames.map((name, i) => {
      const row: Record<string, unknown> = { key: String(i), category: name };
      for (const c of configured) {
        row[c.id] = Math.round(150 + (i * 37 + c.id.length * 13) % 300);
      }
      return row;
    });

    if (state.showAverageRow && dataRows.length > 0) {
      const avgRow: Record<string, unknown> = { key: "avg", category: "Average" };
      for (const c of configured) {
        const vals = dataRows.map((r) => r[c.id] as number);
        avgRow[c.id] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
      }
      dataRows.unshift(avgRow);
    }

    let rows = dataRows;
    if (showSeparators) {
      const nonAvgRows = dataRows.filter((r) => r.key !== "avg");
      const avgRowsList = dataRows.filter((r) => r.key === "avg");

      const posGroups = new Map<string, typeof nonAvgRows>();
      for (const row of nonAvgRows) {
        const pos = MOCK_PLAYER_POSITIONS[row.category as string] ?? "Other";
        if (!posGroups.has(pos)) posGroups.set(pos, []);
        posGroups.get(pos)!.push(row);
      }

      const withSeparators: typeof dataRows = [];
      let lastPos: string | null = null;
      for (const row of nonAvgRows) {
        const pos = MOCK_PLAYER_POSITIONS[row.category as string] ?? "Other";
        if (pos !== lastPos) {
          const group = posGroups.get(pos) ?? [];
          const sepRow: Record<string, unknown> = { key: `__pos_sep_${pos}`, category: pos, __is_position_separator: true };
          for (const c of configured) {
            const vals = group.map((r) => r[c.id] as number).filter((v) => typeof v === "number");
            sepRow[c.id] = vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
          }
          withSeparators.push(sepRow);
          lastPos = pos;
        }
        withSeparators.push(row);
      }
      rows = [...avgRowsList, ...withSeparators];
    }

    return { columns: antdColumns, rows };
  }, [state.columns, state.showAverageRow, state.separateByPosition, state.groupBy, metrics]);

  const configuredCount = state.columns.filter((c) => c.selectedMetricId != null).length;
  const dateLabel = resolveDateRange(state);
  const totalSelected = state.selectedSubjects.length + state.selectedDrills.length;

  const form = (
    <>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8ecf0" }}>
        <Space size={8} align="center" style={{ width: "100%" }}>
          <Text style={{ fontSize: 12, minWidth: 40 }}>Title</Text>
          <SlicerTokenInput
            size="small"
            placeholder="e.g. Sprint Metrics"
            value={state.title}
            onChange={(title) => dispatch({ type: "SET_TITLE", title })}
            style={{ flex: 1 }}
          />
        </Space>
      </div>

      <Collapse
        accordion
        activeKey={state.expandedKey}
        onChange={(key) => dispatch({ type: "SET_EXPANDED_KEY", key })}
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        <Panel
          key="__date_range"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">1</span>
              <Text strong style={{ fontSize: 13 }}>Date Range</Text>
              <SectionSummary kind="date" controllerId={state.dateControllerId} own={dateLabel} />
            </div>
          }
        >
          <DateRangeForm
            allowLatestN
            bindable={isSectionBindable("table", "date")}
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_DATE_RANGE", patch })}
          />
        </Panel>

        <Panel
          key="__players"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">2</span>
              <Text strong style={{ fontSize: 13 }}>Players</Text>
              <SectionSummary kind="subject" controllerId={state.subjectControllerId}
                own={totalSelected > 0 ? `${totalSelected} selected` : "Full Team"}
                getSubjectEntities={getSubjectEntities} />
            </div>
          }
        >
          <PlayersForm
            hideDrills={state.columns.some((c) => c.countSpec)}
            bindable={isSectionBindable("table", "subject")}
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_PLAYERS", patch })}
            getSubjectEntities={getSubjectEntities}
          />
          <div
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
              margin: "8px 0 0",
            }}
          >
            <div>
              <Text strong style={{ fontSize: 12 }}>Exclude Injured Players</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Hide injured athletes' rows.
              </Text>
            </div>
            <Switch
              size="small"
              checked={state.excludeInjuredPlayers}
              onChange={(v) => dispatch({ type: "SET_EXCLUDE_INJURED_PLAYERS", value: v })}
            />
          </div>
        </Panel>

        {state.columns.map((col, index) => {
          const metricName = metrics.find((m) => m.id === col.selectedMetricId)?.name;
          return (
            <Panel
              key={col.id}
              header={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="awm-step-number">{index + 3}</span>
                  <Text strong style={{ fontSize: 13 }}>
                    Column {index + 1}
                  </Text>
                  {metricName && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {metricName}
                    </Text>
                  )}
                </div>
              }
              extra={
                state.columns.length > 1 ? (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch({ type: "REMOVE_COLUMN", index });
                    }}
                    style={{ marginRight: -4 }}
                  />
                ) : undefined
              }
            >
              <TableColumnForm
                col={col}
                onChange={(patch) =>
                  dispatch({ type: "UPDATE_COLUMN", index, patch })
                }
              />
            </Panel>
          );
        })}
      </Collapse>

      <Button
        type="text"
        icon={<PlusOutlined />}
        onClick={() => dispatch({ type: "ADD_COLUMN" })}
        style={{ width: "100%", fontWeight: 600, color: "#1677ff" }}
      >
        Add Column
      </Button>

      <div style={{ padding: "12px 16px", borderTop: "1px solid #e8ecf0", display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "8px 10px",
            border: "1px solid #e0e5eb",
            borderRadius: 8,
            background: "#fafbfc",
          }}
        >
          <div>
            <Text strong style={{ fontSize: 12 }}>Show Average Row</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Append a row showing column averages.
            </Text>
          </div>
          <Switch
            size="small"
            checked={state.showAverageRow}
            onChange={(v) => dispatch({ type: "SET_SHOW_AVERAGE_ROW", value: v })}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "8px 10px",
            border: "1px solid #e0e5eb",
            borderRadius: 8,
            background: "#fafbfc",
          }}
        >
          <div>
            <Text strong style={{ fontSize: 12 }}>Separate by Position</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {state.groupBy === "player"
                ? "Group players by position with section dividers."
                : "Only available when grouped by Player."}
            </Text>
          </div>
          {state.groupBy === "player" ? (
            <Switch
              size="small"
              checked={state.separateByPosition}
              onChange={(v) => dispatch({ type: "SET_SEPARATE_BY_POSITION", value: v })}
            />
          ) : (
            <LockOutlined style={{ fontSize: 14, color: "rgba(0,0,0,0.25)", marginTop: 2 }} />
          )}
        </div>
      </div>
    </>
  );

  const preview = (
    <>
      <PreviewShell
        title={state.title || "Table Preview"}
        titleAddon={previewData != null ? <PreviewDisclaimer /> : undefined}
        hasContent={previewData != null}
        emptyText="Select a metric to see a preview"
        summaryText={
          configuredCount === 0
            ? "Pick a metric to see a preview..."
            : `${configuredCount} of ${state.columns.length} columns configured`
        }
      >
        {previewData && (
          <Table
            columns={previewData.columns}
            dataSource={previewData.rows}
            pagination={false}
            size="small"
            bordered={false}
            style={{ width: "100%" }}
            rowClassName={(record) => record.key === "avg" ? "avg-row" : ""}
            onRow={(record) =>
              record.__is_position_separator
                ? {
                    style: {
                      backgroundColor: "#f0f2f5",
                      borderTop: "1px solid #d0d5dd",
                    },
                  }
                : {}
            }
          />
        )}
      </PreviewShell>

      <GroupByBar
        value={state.groupBy}
        disabledGroupBys={
          state.columns.some((c) => c.countSpec)
            ? { drill: "A count cannot be grouped by drill: none of the counted tables carries a drill relation." }
            : undefined
        }
        onChange={(groupBy) => dispatch({ type: "SET_GROUP_BY", groupBy })}
      />
    </>
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
